import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api.js";
import { navigate } from "../lib/router.js";
import { Button } from "../components/Button.js";
import { BlazeMark } from "../components/Panel.js";

/**
 * Explicit redemption — never automatic in a mount effect. The user chooses
 * to Continue, then we POST the token.
 *
 * The token arrives in the URL hash (#/confirm?token=…). It is copied into
 * component state (memory) on first render, then the hash is rewritten to
 * keep only the #/confirm route so the token is removed from browser history.
 * The route must NOT be removed — if the URL became "/", the app would
 * re-route to the sign-in screen before redemption (this happened in prod).
 */
export function ConfirmScreen({ token: rawToken }: { token: string }) {
  // Hold the token in state so it survives the history.replaceState below.
  const [token] = useState(() => rawToken);
  const [status, setStatus] = useState<"idle" | "busy" | "error" | "consumed" | "done">(
    () => (rawToken ? "idle" : "error"),
  );
  const [message, setMessage] = useState("");
  const submitted = useRef(false);

  useEffect(() => {
    // Remove the token from the URL fragment after we've captured it in
    // memory, but keep the #/confirm route itself.
    if (rawToken && window.location.hash.includes("token=")) {
      window.history.replaceState(null, "", "#/confirm");
    }
  }, [rawToken]);

  async function onContinue() {
    if (!token || submitted.current) return;
    submitted.current = true;
    setStatus("busy");
    try {
      await api.redeem(token, crypto.randomUUID());
      setStatus("done");
      // Full page load, not a hash-only change: the session cookie was just
      // set, and App must re-run its /api/me check to pick up the session.
      window.location.assign("/");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "This link is no longer valid.";
      setStatus(/expired|consumed|no longer/i.test(msg) ? "consumed" : "error");
      setMessage(msg);
      submitted.current = false;
    }
  }

  return (
    <div className="min-h-dvh shell-surface px-[var(--spacing-gutter)] py-8 max-w-md mx-auto flex flex-col justify-center">
      <BlazeMark solid className="mb-4" />
      <h1 className="font-display text-[28px] text-granite-900 dark:text-granite-50 mb-2">
        {status === "consumed" ? "Link already used" : status === "error" ? "Link not valid" : "Sign in"}
      </h1>
      <p className="font-text text-granite-600 dark:text-granite-300 mb-6 leading-relaxed">
        {status === "consumed"
          ? "This sign-in link has already been used. Request a fresh link to continue."
          : status === "error"
            ? message || "This link is missing or malformed. Request a fresh sign-in link."
            : "Continue to sign in with this link. It can be used once."}
      </p>

      {status !== "consumed" && status !== "error" && (
        <div className="flex flex-col gap-3">
          <Button size="lg" onClick={onContinue} disabled={status === "busy"}>
            {status === "busy" ? "Signing in…" : "Continue"}
          </Button>
          <Button variant="ghost" onClick={() => navigate("/")}>
            Back
          </Button>
        </div>
      )}

      {(status === "consumed" || status === "error") && (
        <Button size="lg" onClick={() => navigate("/")}>
          Request a new link
        </Button>
      )}
    </div>
  );
}
